import { platform, REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { controlLockRevision, getControlToken, requiresControlToken } from './controlToken.ts'
import { toApiError, toTransportError } from './errors.ts'

/**
 * HTTP 클라이언트.
 *
 * 🔴 **`fetch` 를 직접 부르지 않는다.** 플랫폼 포트(`platform.http`)를 거친다 —
 *    Tauri 에서 CORS 때문에 `plugin-http` 구현으로 갈아끼워야 하는데, 그때 이 파일이 안 바뀌게 하려는 것이다.
 * 🔴 **인스턴스를 가른다.** 통합 콘솔(L2, Bearer)과 모듈 로컬(L3, mTLS·무인증)은 인증 축이 달라서
 *    하나로 합치면 **모듈 로컬 요청에 L2 토큰이 새어 나간다.** 팩토리만 공유한다.
 */

export type QueryValue = string | number | boolean | undefined | null

export interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** 계약 경로. base(`/api/v1`) 를 뺀 것 — 예: `/batches/{id}/start` 가 아니라 `/batches/abc/start` */
  path: string
  query?: Record<string, QueryValue | readonly QueryValue[]>
  body?: unknown
  /** 기본은 조회 타임아웃. 명령은 `REQUEST_TIMEOUT_MS.command` 를 넘긴다 */
  timeoutMs?: number
  signal?: AbortSignal
  /**
   * 401 을 만나면 재시도할 수 있는 요청인가.
   * 🔴 GET 과 **멱등키(`command_id`)를 가진 명령**만 true 다. 계약이 「같은 `command_id` 재수신은
   *    기존 결과를 재반환」으로 멱등을 보장하기 때문이다. 그 외 쓰기는 다시 시도를 사용자에게 맡긴다.
   *    ⚠ 재시도할 때 `command_id` 를 새로 만들지 않는다 — 멱등이 깨진다.
   */
  retryOn401?: boolean
}

export interface ApiClientOptions {
  /** 예: `/api/v1` 또는 `https://module.local/api/v1` */
  baseUrl: string
  /** Bearer 토큰 공급자. 모듈 로컬은 넘기지 않는다(계약상 mTLS 또는 인증 없음) */
  getAccessToken?: () => string | null
  /** 401 에서 한 번 갱신을 시도한다. `true` 면 원요청을 1회만 재시도한다 */
  refreshAuth?: () => Promise<boolean>
  /**
   * **보내기 전에** 갱신이 필요한가. 🔴 401 을 «나면 고치는 것»이 아니라 «안 나게» 한다.
   *
   * 없으면 종전대로 401 을 받고 나서 고친다. 갱신 자체는 single-flight 라 동시에 뜬 쿼리
   * N 개가 **하나의 약속**을 공유한다(`auth/authApi.refreshSession`) — 회전형 refresh 토큰이
   * 동시에 N 번 돌면 서로를 무효화해 **세션이 죽는다.**
   */
  shouldRefreshBeforeSend?: () => boolean
  /** `X-Control-Token` 공급자. 기본은 이 패키지의 리스 보관소 */
  getControlToken?: () => string | null
  /**
   * 409 `CONTROL_LOCK_HELD` 를 받았다.
   *
   * 🔴 **여기 한 곳에서만 본다.** 명령 훅마다 손으로 달면 새 훅이 빠뜨리고, 빠뜨린 화면은
   *    락을 잃은 뒤에도 버튼이 열린 채로 남는다 — 그게 곧 낙관적 활성이다.
   * ⚠ 모듈 로컬(L3) 클라이언트에는 **넘기지 않는다.** 거기 락은 L2 제어락이 아니다.
   */
  onControlLockDenied?: (denial: {
    reason?: string | undefined
    holder?: string | undefined
    expiresAt?: string | undefined
  }) => void
}

export interface ApiClient {
  request<T>(options: RequestOptions): Promise<T>
  /** 파일 내려받기처럼 본문을 우리가 파싱하지 않는 경우 */
  requestRaw(options: RequestOptions): Promise<Response>
  readonly baseUrl: string
}

function buildQuery(query: RequestOptions['query']): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    const values = Array.isArray(value) ? value : [value]
    for (const item of values) {
      if (item === undefined || item === null) continue
      params.append(key, String(item))
    }
  }
  const qs = params.toString()
  return qs === '' ? '' : `?${qs}`
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined
  const text = await response.text()
  if (text === '') return undefined
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '')
  const controlTokenOf = options.getControlToken ?? getControlToken
  const responseRevisions = new WeakMap<Response, number>()

  async function send(request: RequestOptions, allowRefresh: boolean): Promise<Response> {
    /**
     * 🔴 **만료가 임박했으면 먼저 갱신한다.** 판정은 토큰 보관소가 하고(`accessTokenNeedsRefresh`)
     *    여기는 부르기만 한다 — 「임박」의 정의가 두 곳에 생기지 않게.
     * ⚠ **재시도 경로(`allowRefresh === false`)에서는 안 한다.** 방금 갱신하고 다시 온 자리라
     *   또 부르면 회전형 토큰이 두 번 돈다.
     * ⚠ 실패해도 그냥 보낸다 — 서버가 401 로 답하게 두고 그 아래 경로가 한 번 더 시도한다.
     *   여기서 던지면 **서버가 아직 받아 줬을 요청**까지 우리가 막는 것이다.
     */
    if (
      allowRefresh &&
      options.refreshAuth !== undefined &&
      options.shouldRefreshBeforeSend?.() === true
    ) {
      await options.refreshAuth()
    }

    const headers = new Headers({ Accept: 'application/json' })
    if (request.body !== undefined) headers.set('Content-Type', 'application/json')

    const accessToken = options.getAccessToken?.() ?? null
    if (accessToken !== null) headers.set('Authorization', `Bearer ${accessToken}`)

    // 🔴 표에 있는 경로에만 붙인다. 없으면 붙이지 않고 서버가 거부하게 둔다(상태 추론 금지)
    if (requiresControlToken(request.method, request.path)) {
      const lease = controlTokenOf()
      if (lease !== null) headers.set('X-Control-Token', lease)
    }

    const timeoutMs = request.timeoutMs ?? REQUEST_TIMEOUT_MS.query
    const init: RequestInit = {
      method: request.method,
      headers,
      signal: request.signal ?? AbortSignal.timeout(timeoutMs),
      ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
    }

    let response: Response
    const lockRevision = controlLockRevision()
    try {
      response = await platform.http.fetch(
        `${baseUrl}${request.path}${buildQuery(request.query)}`,
        init,
      )
      responseRevisions.set(response, lockRevision)
    } catch (cause) {
      throw toTransportError(cause)
    }

    if (
      response.status === 401 &&
      allowRefresh &&
      options.refreshAuth !== undefined &&
      (request.method === 'GET' || request.retryOn401 === true)
    ) {
      const refreshed = await options.refreshAuth()
      if (refreshed) return send(request, false)
    }

    return response
  }

  /**
   * 오류 하나를 만든다. 🔴 **제어락 거절을 여기서 낚아챈다** — 던지기 전에 보관소에 알려
   * 버튼이 즉시 닫히게 한다. 화면이 `onError` 를 달았든 안 달았든 상태는 갱신된다.
   */
  function reject(response: Response, body: unknown) {
    const error = toApiError(response, body)
    const denied = options.onControlLockDenied
    if (
      denied !== undefined &&
      error.status === 409 &&
      error.error_code === 'CONTROL_LOCK_HELD' &&
      responseRevisions.get(response) === controlLockRevision()
    ) {
      const context = error.context ?? {}
      denied({
        // 🔴 가드가 준 `context.reason` 만 나른다. 없으면 **없는 대로** 넘긴다 —
        //    `/control-token` 획득·갱신은 문맥을 안 싣는다(실측). 지어내지 않는다.
        reason: typeof context['reason'] === 'string' ? context['reason'] : undefined,
        holder: typeof context['holder'] === 'string' ? context['holder'] : undefined,
        expiresAt: typeof context['expires_at'] === 'string' ? context['expires_at'] : undefined,
      })
    }
    return error
  }

  return {
    baseUrl,

    async request<T>(request: RequestOptions): Promise<T> {
      const response = await send(request, true)
      const body = await readBody(response)
      if (!response.ok) throw reject(response, body)
      return body as T
    },

    async requestRaw(request: RequestOptions): Promise<Response> {
      const response = await send(request, true)
      if (!response.ok) throw reject(response, await readBody(response))
      return response
    },
  }
}

/**
 * 인증 없이 나가는 한 방(로그인 · 갱신).
 * 클라이언트를 거치지 않는 이유는 순환 때문이다 — 갱신이 클라이언트를 부르고 클라이언트가
 * 다시 갱신을 부르는 고리를 만들지 않는다. 계약상 두 경로는 `security: []` 라 헤더가 필요 없다.
 */
export async function postJsonUnauthenticated<T>(
  baseUrl: string,
  path: string,
  body: unknown,
  timeoutMs = REQUEST_TIMEOUT_MS.query,
): Promise<T> {
  let response: Response
  try {
    response = await platform.http.fetch(`${baseUrl.replace(/\/+$/, '')}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(timeoutMs),
    })
  } catch (cause) {
    throw toTransportError(cause)
  }

  const parsed = await readBody(response)
  if (!response.ok) throw toApiError(response, parsed)
  return parsed as T
}
